import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'modules/common/components/Modal/Modal';
import { formValidationErrorType } from 'modules/common/enums';
import SubHeader from 'modules/common/components/SubHeader/SubHeader';
import { status } from 'constants/general';
import servicesStrings from 'modules/services/strings';
import AddOrEditServiceComponent from 'modules/services/components/AddOrEditServiceComponent/AddOrEditServiceComponent';
import {
  addOrEditService as addOrEditServiceAction,
  deleteService as deleteServiceAction,
  fetchServicesList,
  resetAddOrEditService,
} from 'modules/services/actions';
import ServicesList from 'modules/services/components/ServicesList/ServicesList';
import Pagination from 'modules/common/components/Pagination/Pagination';
import Loader from 'modules/common/components/Loader/Loader';
import ServicesFilter from 'modules/services/components/ServicesFilter/ServicesFilter';
import {
  LoaderWrapper,
  NoResultsText,
  NoResultsWrapper,
  PaginationWrapper,
  ServiceDeleteConfirmTitle,
  ServicesWrapper,
  extendDeleteConfirmModalBtn,
} from './ServicesContainer.style';
// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFields = ['cptCode', 'englishName', 'arabicName', 'masterService'];
// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################
const ServicesContainer = ({ initialServicesListApiCallBody }) => {
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const {
    servicesList,
    masterServicesList,
    addOrEditService,
    deleteService,
    specialitiesList,
    subContext,
  } = useSelector(state => ({
    servicesList: state.services.servicesList,
    masterServicesList: state.masterServices.masterServicesList,
    addOrEditService: state.services.addOrEditService,
    deleteService: state.services.deleteService,
    specialitiesList: state.services.specialitiesList,
    subContext: state.common.subContext,
  }));

  const totalResultsCount = servicesList?.details?.totalCount;
  const numberOfPages = (totalResultsCount && Math.ceil(totalResultsCount / 10)) || 1;
  const servicesListArr = servicesList?.details?.results;

  const dispatch = useDispatch();
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const [servicesListApiCallBody, setServicesListApiCallBody] = useState({});
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [formFieldsValues, setFormFieldsValues] = useState({
    cptCode: '',
    englishName: '',
    arabicName: '',
    masterService: '',
    categoryKey: subContext,
    status: 'active',
    specialities: [],
    details: '',
    detailsEn: '',
    preparations: [],
    isPopular: false,
    imageUrl: null,
  });
  const [formFieldsValidation, setFormFieldsValidation] = useState({
    cptCode: formValidationErrorType.valid,
    englishName: formValidationErrorType.valid,
    arabicName: formValidationErrorType.valid,
    masterService: formValidationErrorType.valid,
  });
  const [selectedService, setSelectedService] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  useEffect(() => {
    setFormFieldsValues(prevState => ({
      ...prevState,
      categoryKey: subContext,
    }));

    const data = {
      ...initialServicesListApiCallBody,
    };
    data.categoryKey = subContext;
    if (data.searchTerm) delete data.searchTerm;
    document.getElementById('search-bar-input').value = '';
    setServicesListApiCallBody({ ...data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subContext]);

  useEffect(() => {
    if (Object.keys(servicesListApiCallBody).length > 0) {
      const data = {
        ...servicesListApiCallBody,
      };
      const body = {
        data,
      };
      dispatch(fetchServicesList(body));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, servicesListApiCallBody]);
  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  const searchBarInputTimeout = useRef(null);
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################
  const handleConfirmDetailsModalClick = functionCallBack => {
    let isFormValid = true;

    // we get all required fields and then make sure it has a value
    // and if it has a special regex requirement we validate it against that regex
    requiredFormFields.forEach(fieldName => {
      let isFieldValid = formValidationErrorType.valid;

      if (!formFieldsValues[fieldName].toString()) {
        isFieldValid =
          (!formFieldsValues[fieldName] && formValidationErrorType.empty) ||
          formValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: isFieldValid,
      }));
    });

    if (isFormValid === false) return;

    const actionBody = {
      data: {
        cpt: formFieldsValues.cptCode,
        serviceNameAr: formFieldsValues.arabicName,
        serviceNameEn: formFieldsValues.englishName,
        masterServiceKey: formFieldsValues.masterService,
        categoryKey: formFieldsValues.categoryKey,
        StatusKey: formFieldsValues.status,
        serviceSpecialities: formFieldsValues.specialities,
        details: formFieldsValues.details,
        detailsEn: formFieldsValues.detailsEn,
        servicePreparations: formFieldsValues.preparations,
        isPopular: formFieldsValues.isPopular,
        imageUrl: formFieldsValues.imageUrl,
        ...(selectedService?.serviceKey ? { serviceKey: selectedService.serviceKey } : {}),
      },
      callbackAction: functionCallBack,
    };
    dispatch(addOrEditServiceAction(actionBody));
  };

  const handleCancelDetailsModalClick = () => {
    setFormFieldsValidation({
      cptCode: formValidationErrorType.valid,
      englishName: formValidationErrorType.valid,
      arabicName: formValidationErrorType.valid,
      masterService: formValidationErrorType.valid,
    });
    setFormFieldsValues({
      cptCode: '',
      englishName: '',
      arabicName: '',
      masterService: '',
      categoryKey: subContext,
      status: 'active',
      specialities: [],
      details: '',
      detailsEn: '',
      imageUrl: null,
      preparations: [],
    });
    setSelectedService(null);
    setIsDetailsModalOpen(false);
    dispatch(resetAddOrEditService());
  };

  const handleEditServiceBtnClick = service => {
    const {
      cpt,
      serviceNameEn,
      serviceNameAr,
      details,
      masterServiceKey,
      serviceSpecialities,
      servicePreparations,
      categoryKey,
      statusKey,
      isPopular,
      detailsEn,
      imageUrl,
    } = service;
    const tempServiceSpecialities = serviceSpecialities.map(speciality => {
      const specialityName = specialitiesList?.details?.find(
        spec => spec.SpecialityKey === speciality.specialityKey,
      )?.SpecialityNameEnglish;
      return {
        key: speciality.specialityKey,
        name: specialityName,
      };
    });
    setFormFieldsValues({
      cptCode: cpt,
      englishName: serviceNameEn,
      arabicName: serviceNameAr,
      masterService: masterServiceKey,
      categoryKey,
      status: statusKey,
      specialities: [...tempServiceSpecialities],
      details,
      detailsEn,
      preparations: JSON.parse(JSON.stringify(servicePreparations)),
      isPopular: !!isPopular,
      imageUrl,
    });
    setSelectedService(service);
    setIsDetailsModalOpen(true);
  };

  const handleDeleteServiceBtnClick = service => {
    setSelectedService(service);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteServiceConfirmBtnClick = functionCallBack => {
    const actionBody = {
      data: selectedService,
      callbackAction: functionCallBack,
    };
    dispatch(deleteServiceAction(actionBody));
  };

  const handleCancelDeleteModalClick = () => {
    setSelectedService(null);
    setIsDeleteModalOpen(false);
  };

  const handleOrderPaginationClick = selectedPageNumber => {
    const data = {
      ...servicesListApiCallBody,
    };
    data.pageNum = selectedPageNumber;
    setServicesListApiCallBody({ ...data });
  };

  const submitSearchQuery = (value, reset) => {
    const data = {
      ...servicesListApiCallBody,
      pageNum: 1,
    };
    if (value) data.searchTerm = value;
    if (reset) delete data.searchTerm;
    setServicesListApiCallBody({ ...data });
  };

  const handleSearchBarInputChange = event => {
    const { value } = event.target;
    const searchQueryMinLength = 3;

    if (value.length > 30) return;

    if (searchBarInputTimeout.current) clearTimeout(searchBarInputTimeout.current);

    if (value && value.length >= searchQueryMinLength) {
      searchBarInputTimeout.current = setTimeout(() => {
        clearTimeout(searchBarInputTimeout.current);
        submitSearchQuery(value);
      }, 300);
    } else if (value.length < searchQueryMinLength && servicesListApiCallBody.searchTerm) {
      submitSearchQuery(value, true);
    }
  };

  const handleDropdownInputChange = (value, key) => {
    const data = {
      ...servicesListApiCallBody,
      pageNum: 1,
    };
    if (value) data[key] = value;
    if (value === 'all') delete data[key];
    setServicesListApiCallBody({ ...data });
  };

  const handleMultiSelectDropDownInputChange = (fieldName, selectedObj) => {
    setServicesListApiCallBody(prevValue => {
      const fieldsObj = { ...prevValue, pageNum: 1 };
      if (selectedObj === 'all') delete fieldsObj[fieldName];
      else fieldsObj[fieldName] = selectedObj;
      return fieldsObj;
    });
  };

  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  const renderServicesList = () => {
    if (servicesList.status === status.SHOULD_CALL_API || servicesList.status === status.FETCHING)
      return (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      );

    if (servicesList.status === status.SUCCESS && servicesListArr?.length > 0)
      return (
        <>
          <ServicesList
            servicesListArr={servicesListArr}
            handleEditServiceBtnClick={handleEditServiceBtnClick}
            handleDeleteServiceBtnClick={handleDeleteServiceBtnClick}
            specialitiesList={specialitiesList?.details}
            subContext={subContext}
          />
          {numberOfPages > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={servicesListApiCallBody.pageNum}
                totalCount={numberOfPages}
                handleOrderPaginationClick={handleOrderPaginationClick}
              />
            </PaginationWrapper>
          )}
        </>
      );

    return (
      <NoResultsWrapper>
        <NoResultsText>{servicesStrings.servicesList.servicesGridNoResultFound}</NoResultsText>
      </NoResultsWrapper>
    );
  };
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################
  return (
    <ServicesWrapper>
      <SubHeader
        subHeaderTitle="Service"
        totalCount={totalResultsCount}
        handleAddBtnClick={() => setIsDetailsModalOpen(true)}
        addBtnText={servicesStrings.serviceDetailsContent.addService}
      />
      <ServicesFilter
        handleSearchBarInputChange={handleSearchBarInputChange}
        handleDropdownInputChange={handleDropdownInputChange}
        handleMultiSelectDropDownInputChange={handleMultiSelectDropDownInputChange}
        servicesListApiCallBody={servicesListApiCallBody}
        masterServicesList={masterServicesList}
        specialitiesList={specialitiesList}
      />
      {renderServicesList()}
      <Modal
        data-testid="AddOrEditServiceModal"
        title={
          selectedService !== null
            ? servicesStrings.serviceDetailsContent.editService
            : servicesStrings.serviceDetailsContent.addService
        }
        onClose={() => handleCancelDetailsModalClick()}
        toggle={isDetailsModalOpen}
        onConfirmHandler={() => {
          handleConfirmDetailsModalClick(() => {
            handleCancelDetailsModalClick();
            const data = {
              ...initialServicesListApiCallBody,
              pageNum: servicesListApiCallBody.pageNum,
              categoryKey: subContext,
            };
            setServicesListApiCallBody({ ...data });
          });
        }}
        confirmActionLabel={servicesStrings.serviceDetailsContent.confirmButtonLabel}
        confirmActionLoading={addOrEditService.status === status.FETCHING}
        onCancelHandler={() => handleCancelDetailsModalClick()}
        cancelActionLabel={servicesStrings.serviceDetailsContent.cancelButtonLabel}
        scrollContent={formFieldsValues.preparations}
        errorText={addOrEditService?.error}
      >
        <AddOrEditServiceComponent
          formFieldsValues={formFieldsValues}
          setFormFieldsValues={setFormFieldsValues}
          formFieldsValidation={formFieldsValidation}
          masterServicesList={masterServicesList?.details?.results}
          subContext={subContext}
          specialitiesList={specialitiesList?.details}
          serviceKey={selectedService?.serviceKey}
        />
      </Modal>
      <Modal
        data-testid="DeleteServiceModal"
        title={servicesStrings.serviceDelete.serviceDeleteModalTitle}
        onClose={() => handleCancelDeleteModalClick()}
        toggle={isDeleteModalOpen}
        onConfirmHandler={() =>
          handleDeleteServiceConfirmBtnClick(() => {
            handleCancelDeleteModalClick();
            const data = {
              ...initialServicesListApiCallBody,
              categoryKey: subContext,
            };
            if (servicesListArr?.length > 1) data.pageNum = servicesListApiCallBody.pageNum;
            else if (servicesListApiCallBody.pageNum > 1)
              data.pageNum = servicesListApiCallBody.pageNum - 1;
            setServicesListApiCallBody({ ...data });
          })
        }
        confirmActionLabel={servicesStrings.serviceDelete.serviceDeleteModalConfirmBtn}
        confirmActionLoading={deleteService.status === status.FETCHING}
        extendConfirmButtonStyle={extendDeleteConfirmModalBtn}
        onCancelHandler={() => handleCancelDeleteModalClick()}
        cancelActionLabel={servicesStrings.serviceDetailsContent.cancelButtonLabel}
      >
        <ServiceDeleteConfirmTitle>
          {servicesStrings.serviceDelete.serviceDeleteModalConfirmTitle(
            selectedService?.serviceNameEn,
          )}
        </ServiceDeleteConfirmTitle>
      </Modal>
    </ServicesWrapper>
  );
};
// ###############################################################
// <----------------------- End Return UI ----------------------->
// ###############################################################

ServicesContainer.propTypes = {
  initialServicesListApiCallBody: PropTypes.shape({}).isRequired,
};

export default ServicesContainer;
